import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Filter } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatDate, formatMoney } from '../lib/format';
import { postBankReconcile } from '../lib/posting';
import type { Account, BankAccount, BankTransaction, TaxCode } from '../lib/types';

interface Row extends BankTransaction {
  proposed_account_id?: string;
}

export default function BankReconcile() {
  const { bankAccountId } = useParams();
  const { currentOrg } = useOrg();
  const [bankAccount, setBankAccount] = useState<BankAccount | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [showReconciled, setShowReconciled] = useState(false);
  const [busy, setBusy] = useState<string | null>(null); // txn id

  async function refresh() {
    if (!bankAccountId) return;
    const { data: ba } = await supabase.from('dodobooks_bank_accounts').select('*').eq('id', bankAccountId).maybeSingle();
    setBankAccount(ba as BankAccount);
    const { data: txns } = await supabase
      .from('dodobooks_bank_transactions')
      .select('*')
      .eq('bank_account_id', bankAccountId)
      .order('txn_date', { ascending: false });
    setRows((txns ?? []) as Row[]);
  }

  useEffect(() => {
    if (!currentOrg) return;
    Promise.all([
      supabase.from('dodobooks_accounts').select('*').eq('org_id', currentOrg.id).eq('is_archived', false).order('code'),
      supabase.from('dodobooks_tax_codes').select('*').eq('org_id', currentOrg.id).eq('is_active', true).order('code'),
    ]).then(([a, t]) => {
      setAccounts((a.data ?? []) as Account[]);
      setTaxCodes((t.data ?? []) as TaxCode[]);
    });
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccountId, currentOrg]);

  function suggestAccount(description: string, amount: number): string | undefined {
    // Tiny heuristic — match common SA expense / income patterns.
    const d = description.toLowerCase();
    if (/vodacom|mtn|cell ?c|telkom|rain\b/.test(d)) return accounts.find((a) => a.code === '6020')?.id;
    if (/eskom|electricity|water/.test(d)) return accounts.find((a) => a.code === '6010')?.id;
    if (/sars|vat\b|paye/.test(d)) return accounts.find((a) => a.code === '2100')?.id;
    if (/rent/.test(d)) return accounts.find((a) => a.code === '6000')?.id;
    if (/salary|wages|payroll/.test(d)) return accounts.find((a) => a.code === '6050')?.id;
    if (/fuel|engen|shell|bp|sasol|petrol/.test(d)) return accounts.find((a) => a.code === '6040')?.id;
    if (/bank charge|fee|service fee/.test(d)) return accounts.find((a) => a.code === '6080')?.id;
    if (/insurance/.test(d)) return accounts.find((a) => a.code === '6090')?.id;
    if (/stripe|payfast|peach payments|yoco/.test(d) && amount > 0) return accounts.find((a) => a.code === '4000')?.id;
    if (amount > 0) return accounts.find((a) => a.code === '4900')?.id; // other income
    return accounts.find((a) => a.code === '6900')?.id; // other expense
  }

  function updateRow(id: string, patch: Partial<Row>) {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
  }

  async function reconcileRow(r: Row) {
    if (!currentOrg || !bankAccount) return;
    const offset = r.proposed_account_id ?? suggestAccount(r.description, Number(r.amount));
    if (!offset) return;
    setBusy(r.id);
    const result = await postBankReconcile({
      org_id: currentOrg.id,
      bank_account_gl_id: bankAccount.account_id,
      bank_txn_id: r.id,
      date: r.txn_date,
      amount: Number(r.amount),
      offset_account_id: offset,
      description: r.description,
    });
    setBusy(null);
    if (!result.error) refresh();
  }

  const filtered = rows.filter((r) => showReconciled || !r.reconciled);
  const unrec = rows.filter((r) => !r.reconciled).length;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Link to="/bank" className="btn btn-ghost"><ArrowLeft size={14} /></Link>
          <div>
            <h1 className="h-page">{bankAccount?.bank_name ?? '…'}</h1>
            <p className="text-text-dim text-sm mt-1">
              {bankAccount?.account_number} · {unrec} unreconciled
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowReconciled((v) => !v)} className="btn btn-secondary"><Filter size={14} /> {showReconciled ? 'Hide reconciled' : 'Show all'}</button>
          <Link to="/bank/import" className="btn btn-primary">Import more</Link>
        </div>
      </header>

      <div className="card p-0 overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="text-text-mute text-sm py-12 text-center">
            Nothing to reconcile here. <Link to="/bank/import" className="text-emerald hover:underline">Import a statement.</Link>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="px-4">Date</th>
                <th>Description</th>
                <th className="text-right">Amount</th>
                <th>Categorize as</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const suggested = r.proposed_account_id ?? suggestAccount(r.description, Number(r.amount));
                return (
                  <tr key={r.id} className={r.reconciled ? 'opacity-60' : ''}>
                    <td className="px-4 text-text-dim">{formatDate(r.txn_date)}</td>
                    <td>
                      <div className="text-sm">{r.description}</div>
                      {r.reference && <div className="text-xs text-text-mute font-mono">{r.reference}</div>}
                    </td>
                    <td className={`text-right num ${Number(r.amount) < 0 ? 'text-rust' : 'text-emerald'}`}>{formatMoney(r.amount)}</td>
                    <td>
                      {r.reconciled ? (
                        <span className="pill pill-paid">Reconciled</span>
                      ) : (
                        <select
                          value={suggested ?? ''}
                          onChange={(e) => updateRow(r.id, { proposed_account_id: e.target.value || undefined })}
                          className="w-full text-xs"
                        >
                          <option value="">— pick account —</option>
                          {accounts.map((a) => (
                            <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      {!r.reconciled && (
                        <button
                          onClick={() => reconcileRow(r)}
                          disabled={busy === r.id || !suggested}
                          className="btn btn-primary btn-sm disabled:opacity-50"
                        >
                          <CheckCircle size={12} /> {busy === r.id ? '…' : 'Match'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
